import { useEffect, useRef } from 'react';

/**
 * Marker stamped onto the history entry this hook pushes. It lets a remount (or
 * a reload) tell "this page is already pinned" from a genuine fresh arrival, so
 * the decoy entry is pushed once instead of once per mount.
 */
const BACK_GUARD_KEY = 'foxcomBackGuard';

type GuardedHistoryState = Record<string, unknown> & {
  [BACK_GUARD_KEY]?: boolean;
};

const isGuardEntry = (state: unknown): boolean =>
  !!state &&
  typeof state === 'object' &&
  (state as GuardedHistoryState)[BACK_GUARD_KEY] === true;

/**
 * Clone the current entry on top of itself. The URL is unchanged, so the router
 * sees no navigation — only the history stack grows by the one entry a Back
 * press will be spent on. React Router's own state (`{ key, state }`) is carried
 * over so a pop onto this entry still resolves the same location.
 */
const pinCurrentEntry = () => {
  const current = (window.history.state ?? {}) as GuardedHistoryState;
  window.history.pushState(
    { ...current, [BACK_GUARD_KEY]: true },
    '',
    window.location.href
  );
};

/**
 * NCP-5230: keep the user on the current page when they press the browser's
 * Back button.
 *
 * On the Quote page the application is already complete and the quote already
 * priced, so stepping back into the survey drops the customer onto answers that
 * are locked into the issued quote — confusing state, and abandoned quotes. The
 * quote is meant to be terminal in the forward journey; the in-page "Edit"
 * button is the supported way back into the application.
 *
 * Mechanism: push one extra history entry for the current URL on mount, then
 * push it again on every `popstate`. A Back press pops onto the entry
 * underneath — same URL, so the router re-renders the same route instead of
 * navigating — and the re-push restores the buffer for the next press. Only
 * pops are intercepted, so forward navigation is untouched: "Edit"
 * (`history.push`), the Buy-now redirect and in-page flows all still work.
 *
 * @param onBlocked Called after a Back press has been absorbed, so the caller
 *   can explain why the page did not move.
 * @param enabled Pass false to leave the browser's Back button alone.
 */
export function useBlockBrowserBack(
  onBlocked?: () => void,
  enabled = true
): void {
  // Latest-ref so an inline callback does not re-run the effect below. Tearing
  // the listener down and back up on every render would be harmless, but the
  // effect is the thing that owns the history entry — keep it mount-scoped.
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Skip when we are already sitting on a pinned entry: this page can remount
    // without a navigation (Quote.tsx remounts on its LOB key changing), and
    // pinning again each time would stack up entries the user then has to press
    // Back through once the guard is gone.
    if (!isGuardEntry(window.history.state)) {
      pinCurrentEntry();
    }

    const onPopState = () => {
      // The pop has already happened and the URL is back to this page's URL.
      // Re-pin so the next Back press has an entry to land on, then let the
      // caller explain what just did not happen.
      pinCurrentEntry();
      onBlockedRef.current?.();
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [enabled]);
}

export default useBlockBrowserBack;
